/** Figma `Listbox`(Components, 36:946) · `_Listbox/Option`(36:883) · `_Listbox/PlainItem`(2058:3176) */
export const LISTBOX_SELECTIONS = ['single', 'multiple'] as const;
export type ListboxSelection = (typeof LISTBOX_SELECTIONS)[number];

export const LISTBOX_STATUSES = ['default', 'init', 'empty', 'loading'] as const;
export type ListboxStatus = (typeof LISTBOX_STATUSES)[number];

export const LISTBOX_PLAIN_ITEM_SIZES = ['md', 'sm'] as const;
export type ListboxPlainItemSize = (typeof LISTBOX_PLAIN_ITEM_SIZES)[number];

/** 선택지 하나. Select · MultiSelect · 도메인 선택 상자가 같은 모양으로 넘긴다 */
export interface ListboxOptionItem {
  value: string;
  label: string;
  disabled?: boolean;
}

/** 목록 상자: 흰 면 + 1px 테두리 + 모서리 6 + 위아래 4 + dropdown 그림자 */
export const LISTBOX_BOX_CLASS =
  'relative flex w-full flex-col overflow-y-auto rounded-md border border-border-default bg-bg-surface py-1 shadow-dropdown';

/**
 * 한 번에 보이는 높이의 한계(옵션 6줄 + 위아래 여백 = 250, `size.listbox-max`). 넘치면 목록 안에서 스크롤한다.
 * Figma 는 4줄만 그렸다.
 */
export const LISTBOX_MAX_HEIGHT_CLASS = 'max-h-(--size-listbox-max)';

/** status=init 의 막대 3개 너비(Figma 140 · 116 · 92 = `size.skeleton.*`, DD-19) */
export const LISTBOX_SKELETON_BAR_CLASS = [
  'w-(--size-skeleton-140)',
  'w-(--size-skeleton-116)',
  'w-(--size-skeleton-92)',
] as const;

/* ─── _Listbox/Option ─── */

/** 높이 40 · 왼쪽 20 · 오른쪽 12 · 사이 8 · 글자 body */
export const OPTION_BASE_CLASS =
  'flex h-(--size-control-md) w-full shrink-0 items-center gap-2 pr-3 pl-5 text-left typo-body select-none';

type OptionClassMap = Record<ListboxSelection, { off: string; on: string }>;

/** 선택 모양(state=default). single 선택됨 = primary 채움, multiple 은 체크 상자로만 */
export const OPTION_SELECTED_CLASS: OptionClassMap = {
  single: {
    off: 'text-fg-default',
    on: 'bg-bg-primary text-on-primary',
  },
  multiple: {
    off: 'text-fg-default',
    on: 'text-fg-default',
  },
};

/** state=hover — 마우스(`hover:`) */
export const OPTION_HOVER_CLASS: OptionClassMap = {
  single: {
    off: 'hover:bg-bg-primary-subtle',
    on: 'hover:bg-bg-primary-hover',
  },
  multiple: {
    off: 'hover:bg-bg-primary-subtle',
    on: 'hover:bg-bg-hover',
  },
};

/** 키보드로 가리킨 줄(`active`) — hover 와 같은 모양. `OPTION_SELECTED_CLASS` 대신 쓴다(cn 은 겹친 클래스를 합치지 않는다) */
export const OPTION_ACTIVE_CLASS: OptionClassMap = {
  single: {
    off: 'bg-bg-primary-subtle text-fg-default',
    on: 'bg-bg-primary-hover text-on-primary',
  },
  multiple: {
    off: 'bg-bg-primary-subtle text-fg-default',
    on: 'bg-bg-hover text-fg-default',
  },
};

/** state=disabled — `OPTION_SELECTED_CLASS` 대신 쓴다 */
export const OPTION_DISABLED_CLASS: OptionClassMap = {
  single: {
    off: 'cursor-not-allowed text-fg-disabled',
    on: 'cursor-not-allowed bg-bg-primary-subtle text-fg-disabled',
  },
  multiple: {
    off: 'cursor-not-allowed text-fg-disabled',
    on: 'cursor-not-allowed text-fg-disabled',
  },
};

/* ─── _Listbox/PlainItem ─── */

/**
 * 높이 md 44 · sm 32. 44 는 한 곳에서만 쓰는 값이라 raw 예외(R4 — 가까운 control.md 40 에 맞추면 모양이 바뀐다).
 * 두 크기를 같은 방식(인라인 style)으로 준다.
 */
export const PLAIN_ITEM_HEIGHT_PX: Record<ListboxPlainItemSize, number> = { md: 44, sm: 32 };

export const PLAIN_ITEM_BASE_CLASS =
  'flex w-full items-center justify-center border-b border-border-default px-2 text-center';

export const PLAIN_ITEM_SELECTED_CLASS = {
  off: 'bg-transparent text-fg-muted typo-caption hover:bg-bg-hover',
  on: 'bg-bg-memo text-fg-primary typo-caption-strong',
} as const;

/** 트리거와 목록 사이(space/1). portal 위치 계산에 쓴다. inline 은 `gap-1` */
export const LISTBOX_POPUP_GAP_PX = 4;
